import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import { NavLink } from 'react-router-dom';
import { Spring } from 'react-spring/renderprops';
import './styles.css';
import AvatarWithSelection from '../../../../components/AvatarWithSelection';
import ItemFooterActions from '../../../../components/ItemFooterActions';
import LinkCandidateProfile from '../../../../components/LinkCandidateProfile';
import PanelContainer from '../../../../components/PanelContainer';
import TooltipIcon from '../../../../components/TooltipIcon';
import {ANIMATION_TIMEOUT_INOUT} from '../../../../constants/animation';
import {STUDENT_ROLE_ID} from '../../../../constants/roles';
import LinkBuilder from '../../../../helpers/link-builder';

const CandidateItem = props => {
  const {
    item,
    user,
    selected = false,
    actions = {},
    disabled,
    hideImmediateStart = false,
    showDateApply = false,
    isSelectMarkerDisabled,
  } = props;

  const { onSelect, onDeselect } = actions;

  const {
    CandidateID,
    FullName,
    PictureUrl,
    FirmName,
    FirmUrlAlias,
    FirmActive,
    SchoolName,
    OfficeLocationName,
    RoleID,
    RoleName,
    TitleName,
    GroupName,
    IndustryName,
    Dates,
    DegreeName,
    currentSchoolName,
    ScoreName,
    MathSatName,
    VerbalSatName,
    GpaName = 'NA',
    RateValue,
    GmatName,
    ShowExtendedSearchInfo,
    IsRecruiter,
    TurnOffJobsNotify,
    ApplyByName,
    ApplyByRecruiter,
    AppliedDate,
    CandidateCurrentStatus,
    IsOnline,
    AppCount,
    LastActive,
    ImmediateStart,
    DateApply,
    ViewAsRecruiter,
    InDoubt,
    SetupEmailStatus,
    SetupEmailActionStatus,
    Lock,
  } = item;

  const footerActions = useMemo(() => {
    return props.footerActions.map(action => {
      return Lock && Lock.State && action.icon === 'message'
        ? { ...action, disabled: true }
        : action;
    });
  }, [props.footerActions]);

  const handleClickSelectMarker = value => {
    if (!isSelectMarkerDisabled && onSelect && onDeselect) {
      if (value) {
        onDeselect(CandidateID);
      } else {
        onSelect(CandidateID);
      }
    }
  };

  const isStudent = Number(RoleID) === STUDENT_ROLE_ID;

  let title = null;
  let group = null;
  if (isStudent) {
    title = DegreeName ? DegreeName : null;
    group = (
      <span className="candidate-item__text">GMAT: {GmatName || 'NA'}</span>
    );
  } else {
    title = TitleName ? TitleName : null;
    group = GroupName ? (
      IndustryName ? (
        <div className="candidate-item__group">
          <span className="candidate-item__text">{GroupName}</span>
          &nbsp;/&nbsp;
          <span className="candidate-item__text">{IndustryName}</span>
        </div>
      ) : (
        <span className="candidate-item__text">{GroupName}</span>
      )
    ) : null;
  }

  let sat = null;
  if (
    (MathSatName && MathSatName !== 'NA') ||
    (VerbalSatName && VerbalSatName !== 'NA')
  ) {
    sat = `SAT(M/V): ${MathSatName}/${VerbalSatName}`;
  } else {
    sat = `ACT Score: ${ScoreName || 'NA'}`;
  }

  return (
    <Spring
      from={{ opacity: 0 }}
      to={{ opacity: 1 }}
      config={{ duration: ANIMATION_TIMEOUT_INOUT }}
    >
      {props => (
        <div style={props}>
          <PanelContainer styles={{ padding: 0 }}>
            <article
              className="candidate-item"
              style={{ background: disabled ? '#f40' : 'transparent' }}
            >
              <div className="candidate-item__container">
                <div className="candidate-item__avatar">
                  <AvatarWithSelection
                    size={60}
                    name={FullName}
                    image={PictureUrl}
                    selected={selected}
                    onSelect={handleClickSelectMarker}
                  />
                </div>

                <div className="candidate-item__content">
                  <div className="candidate-item__row">
                    <div className="candidate-item__person">
                      <LinkCandidateProfile
                        user={user}
                        id={CandidateID}
                        name={FullName}
                        viewAsRecruiter={ViewAsRecruiter === 'Yes'}
                        isClickByStrongEnabled
                        cls="candidate-item__title"
                        clsSpan="candidate-item__title"
                      />

                      {ShowExtendedSearchInfo && IsRecruiter === 'Yes' && (
                        <TooltipIcon type="recruiter" />
                      )}

                      {TurnOffJobsNotify && (
                        <TooltipIcon type="no-notifications" />
                      )}

                      {ShowExtendedSearchInfo && ApplyByName && (
                        <TooltipIcon
                          iconStyle={
                            ApplyByRecruiter ? null : { color: '#f40' }
                          }
                          type="applied"
                          appliedBy={ApplyByName}
                          appliedDate={AppliedDate}
                        />
                      )}

                      {InDoubt === 'Yes' && <TooltipIcon type="doubt" />}

                      {!!Number(IsOnline) && <TooltipIcon type="online" />}

                      {(SetupEmailStatus === 'Yes' ||
                        SetupEmailActionStatus === 'Yes') && (
                        <TooltipIcon
                          type={
                            SetupEmailActionStatus === 'Yes'
                              ? 'envelopeOpen'
                              : 'envelope'
                          }
                        />
                      )}

                      {Lock && Lock.State && (
                        <TooltipIcon
                          type="locked"
                          lockedBy={Lock.Author}
                          lockedDate={Lock.Time}
                        />
                      )}
                    </div>

                    <div className="candidate-item__rating">
                      {RateValue ? (
                        <>
                          Rated:{' '}
                          <span className="candidate-item__value">
                            {RateValue} / 10
                          </span>{' '}
                        </>
                      ) : (
                        `Not Yet Rated`
                      )}
                    </div>
                  </div>

                  <div className="candidate-item__row">
                    <div className="candidate-item__info">
                      {isStudent || !FirmName ? (
                        <span className="candidate-item__company-span">
                          {currentSchoolName}
                        </span>
                      ) : FirmActive === 'Yes' ? (
                        <NavLink
                          to={LinkBuilder.getFirmLink(FirmUrlAlias)}
                          className="candidate-item__company"
                        >
                          {FirmName}
                        </NavLink>
                      ) : (
                        <span className="candidate-item__company-span">
                          {FirmName}
                        </span>
                      )}
                      <span className="candidate-item__school">
                        {SchoolName}
                      </span>
                      <span className="candidate-item__location">
                        {OfficeLocationName}
                      </span>
                    </div>
                    {showDateApply && DateApply ? (
                      <span className="candidate-item__apply-date">
                        {DateApply}
                      </span>
                    ) : null}
                  </div>
                  <div className="candidate-item__profile">
                    <div className="candidate-item__column">
                      {RoleName && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">
                            {RoleName}
                          </span>
                        </div>
                      )}
                      {title && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">{title}</span>
                        </div>
                      )}
                      {Dates && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">{Dates}</span>
                        </div>
                      )}
                    </div>
                    <div className="candidate-item__column">
                      {group && (
                        <div className="candidate-item__group">{group}</div>
                      )}
                      {sat && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">{sat}</span>
                        </div>
                      )}
                      {GpaName && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">{`GPA: ${GpaName}`}</span>
                        </div>
                      )}
                    </div>

                    <div className="candidate-item__column">
                      <div className="candidate-item__group">
                        <span className="candidate-item__text">
                          LTM Applications:{' '}
                          {
                            <NumberFormat
                              thousandSeparator
                              value={AppCount}
                              displayType={'text'}
                            />
                          }
                        </span>
                      </div>

                      <div className="candidate-item__group">
                        <span className="candidate-item__text">
                          Last Active: {LastActive || 'No data'}
                        </span>
                      </div>

                      {CandidateCurrentStatus && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">
                            {CandidateCurrentStatus}
                          </span>
                        </div>
                      )}

                      {!hideImmediateStart && (
                        <div className="candidate-item__group">
                          <span className="candidate-item__text">
                            Immediate Start: {ImmediateStart}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="candidate-item__footer">
                <ItemFooterActions actions={footerActions} item={item} />
              </div>
            </article>
          </PanelContainer>
        </div>
      )}
    </Spring>
  );
};

CandidateItem.propTypes = {
  item: PropTypes.shape({
    FullName: PropTypes.string.isRequired,
  }).isRequired,
  hideImmediateStart: PropTypes.bool,
  isAllowedViewProfile: PropTypes.bool,
  showDateApply: PropTypes.bool,
  isSelectMarkerDisabled: PropTypes.bool,
};

export default CandidateItem;
